frappe.provide("erpnext.ui");

// @dokos: what an empty restriction list means, per (party type, host doctype). Party documents
// restrict items; Item / Item Group / Brand restrict parties. Built on call so translations are loaded.
const party_item_empty_description = (party_type, doctype) =>
	({
		"Customer:Item": __("Every customer can buy this item."),
		"Customer:Item Group": __("Every customer can buy the items in this group."),
		"Customer:Brand": __("Every customer can buy the items of this brand."),
		"Customer:Customer": __("This customer can buy every item."),
		"Supplier:Item": __("Every supplier can supply this item."),
		"Supplier:Item Group": __("Every supplier can supply the items in this group."),
		"Supplier:Brand": __("Every supplier can supply the items of this brand."),
		"Supplier:Supplier": __("This supplier can supply every item."),
	}[`${party_type}:${doctype}`]);

erpnext.ui.party_item_manager = class PartyItemManager {
	constructor(wrapper, frm, partyType) {
		this.wrapper = wrapper;
		this.frm = frm;
		this.partyType = partyType;
		this.doctype = "Party Specific Item";
		this.isPartyDoc = ["Customer", "Supplier"].includes(this.frm.doctype);
		this.restrictBasedOn = this.isPartyDoc ? "" : this.frm.doctype;

		// @dokos: listing requires read on Party Specific Item; other users would get "Not permitted"
		if (!frappe.model.can_read(this.doctype)) return;
		// @dokos: Sales/Purchase Users only read restrictions; hide actions that would answer 403
		this.can_create = frappe.model.can_create(this.doctype);
		this.can_delete = frappe.model.can_delete(this.doctype);

		this.setup();
	}

	setup() {
		const title = this.isPartyDoc
			? __("{0} restrictions", [__(this.partyType)])
			: __("Limited to {0}", [__(this.partyType).toLowerCase()]);

		const add_button = this.can_create
			? erpnext.dokos.ui.button.html({
					label: __("Add"),
					icon: "plus",
					variant: "ghost",
					size: "sm",
					css_class: "btn-add-party-specific",
			  })
			: "";

		// @dokos: dk-card + dk-sh section header, façade button (keeps .btn-add-party-specific)
		this.wrapper.innerHTML = `
			<div class="party-specific-container dk-card">
				<div class="party-specific-header dk-sh">
					<span class="dk-sh__title">${title}</span>
					<span class="dk-sh__actions">${add_button}</span>
				</div>
				<div class="party-specific-body"></div>
			</div>
		`;
		this.refresh();
		this.bindEvents();
	}

	async refresh() {
		const body = this.wrapper.querySelector(".party-specific-body");
		let filters = { party_type: this.partyType };

		if (this.isPartyDoc) {
			filters.party = this.frm.doc.name;
		} else {
			filters.restrict_based_on = this.restrictBasedOn;
			filters.based_on_value = this.frm.doc.name;
		}

		const entries = await frappe.db.get_list(this.doctype, {
			filters: filters,
			fields: ["name", "party", "restrict_based_on", "based_on_value"],
		});

		// @dokos: Espresso empty state that says what "no restriction" means
		if (!entries.length) {
			$(body)
				.empty()
				.append(
					erpnext.dokos.ui.empty_state({
						icon: "inbox",
						title: __("No restrictions defined."),
						description: party_item_empty_description(this.partyType, this.frm.doctype),
						actions: this.can_create
							? [
									{
										label: __("Add"),
										icon: "plus",
										onclick: () => this.addRow(),
									},
							  ]
							: [],
						css_class: "party-empty-state",
					})
				);
			return;
		}

		// @dokos: shared dk-table primitive, façade pill and ghost icon button
		let html = `
			<table class="dk-table">
				<thead>
					<tr>
						<th>${__("ID / Name")}</th>
						<th>${__("Type")}</th>
						<th class="dk-act"></th>
					</tr>
				</thead>
				<tbody>
		`;

		entries.forEach((row) => {
			const mainValue = this.isPartyDoc ? row.based_on_value : row.party;
			const typeLabel = this.isPartyDoc ? row.restrict_based_on : this.partyType;

			const remove_button = this.can_delete
				? erpnext.dokos.ui.button.html({
						icon: "trash-2",
						variant: "ghost",
						size: "sm",
						title: __("Delete"),
						css_class: "btn-remove-party-specific",
						attrs: { "data-docname": row.name },
				  })
				: "";

			html += `
				<tr>
					<td>
						<a class="party-id-link" data-type="${typeLabel}" data-name="${mainValue}">${mainValue}</a>
					</td>
					<td>${erpnext.dokos.ui.pill.html({ label: __(typeLabel), theme: "gray" })}</td>
					<td class="dk-act">${remove_button}</td>
				</tr>
			`;
		});

		body.innerHTML = `${html}</tbody></table>`;
		this.bindRowEvents();
	}

	bindRowEvents() {
		// Gestion des clics sur les liens
		this.wrapper.querySelectorAll(".party-id-link").forEach((link) => {
			link.onclick = (e) => {
				const { type, name } = e.target.dataset;
				frappe.set_route("Form", type, name);
			};
		});
	}

	bindEvents() {
		const add_button = this.wrapper.querySelector(".btn-add-party-specific");
		if (add_button) add_button.onclick = () => this.addRow();
		this.wrapper.onclick = (e) => {
			const btn = e.target.closest(".btn-remove-party-specific");
			if (btn) this.removeRow(btn.dataset.docname);
		};
	}

	addRow() {
		if (this.frm.is_new()) {
			frappe.msgprint(__("Please save the document first."));
			return;
		}

		let fields = [];
		if (this.isPartyDoc) {
			fields = [
				{
					label: __("Restrict Based On"),
					fieldname: "restrict_based_on",
					fieldtype: "Select",
					options: ["Item", "Item Group", "Brand"],
					default: "Item",
					reqd: 1,
				},
				{
					label: __("Value"),
					fieldname: "based_on_value",
					fieldtype: "Dynamic Link",
					options: "restrict_based_on",
					reqd: 1,
				},
			];
		} else {
			fields = [
				{
					label: __(this.partyType),
					fieldname: "party",
					fieldtype: "Link",
					options: this.partyType,
					reqd: 1,
				},
			];
		}

		const d = new frappe.ui.Dialog({
			title: __("Add Restriction"),
			fields: fields,
			primary_action_label: __("Add"),
			primary_action: async (values) => {
				const doc = {
					doctype: this.doctype,
					party_type: this.partyType,
					party: this.isPartyDoc ? this.frm.doc.name : values.party,
					restrict_based_on: this.isPartyDoc ? values.restrict_based_on : this.restrictBasedOn,
					based_on_value: this.isPartyDoc ? values.based_on_value : this.frm.doc.name,
				};

				await frappe.call({ method: "frappe.client.insert", args: { doc } });
				await this.refresh();
				d.hide();
			},
		});
		d.show();
	}

	async removeRow(docname) {
		frappe.confirm(__("Remove this restriction?"), async () => {
			await frappe.call({
				method: "frappe.client.delete",
				args: { doctype: this.doctype, name: docname },
			});
			await this.refresh();
		});
	}
};
